import { useCallback, useState, type ReactNode } from "react";
import { prettifyApiError } from "@/shared/api";
import { PortalOrderConfirmDialog } from "./components/PortalOrderConfirmDialog";
import {
  fetchPortalPreview,
  placePortalOrders,
  type PortalPreviewResult,
  type PortalSupplierId,
} from "./portalOrdersApi";

export type PortalOrderAction = {
  /** Apre il popup di conferma per gli ordini indicati. NON ordina. */
  start: (orderIds: string[]) => Promise<void>;
  /** Preview in corso: feedback sul bottone che l'ha lanciata. */
  busy: boolean;
  /** Popup aperto o ordini in partenza: gate per ogni altra azione. */
  dialogBusy: boolean;
  /** Il popup di conferma. `null` quando è chiuso: lo renderizza il consumer. */
  dialog: ReactNode;
};

type UsePortalOrderActionOpts = {
  supplierId: PortalSupplierId;
  supplierLabel: string;
  onMessage: (msg: string) => void;
  onError: (msg: string) => void;
  onRefresh: () => void;
};

/**
 * «Ordina sul portale del fornitore», in un posto solo.
 *
 * Lo usano sia la barra azioni (batch sulla selezione) sia la singola card:
 * un invariante duplicato è un invariante che prima o poi diverge, e qui
 * l'invariante è *quando parte una spesa vera* — non è il tipo di regola da
 * tenere in due copie (stessa lezione di `usePickupMailAction`).
 *
 * Due fasi obbligate: `start` carica solo l'anteprima dal portale, l'ordine
 * parte esclusivamente dalla conferma nel dialog.
 */
export function usePortalOrderAction(opts: UsePortalOrderActionOpts): PortalOrderAction {
  // Sink destrutturati e non l'oggetto opts (nuovo letterale a ogni render del
  // caller): tiene stabile l'identità di `start` e non fa ri-renderizzare
  // tutte le OrderCard.
  const { supplierId, supplierLabel, onMessage, onError, onRefresh } = opts;
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<PortalPreviewResult | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const start = useCallback(
    async (orderIds: string[]) => {
      if (orderIds.length === 0) return;
      setBusy(true);
      onMessage("");
      onError("");
      try {
        const result = await fetchPortalPreview(supplierId, orderIds);
        setSendError(null);
        setPreview(result);
      } catch (err) {
        onError(prettifyApiError(err));
      } finally {
        setBusy(false);
      }
    },
    [supplierId, onMessage, onError],
  );

  /** Conferma: da qui il fornitore riceve ordini veri, uno alla volta. */
  async function confirm() {
    if (!preview) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await placePortalOrders(
        supplierId,
        // Reference fotografate nella preview: la selezione può cambiare
        // sotto al dialog, si ordina ESATTAMENTE ciò che è stato confermato.
        // I già ordinati restano fuori (il server li rifiuterebbe comunque).
        preview.orders.filter((o) => !o.already_ordered).map((o) => o.reference),
      );
      const ok = result.ordered.length;
      const sk = result.skipped.length;
      const er = result.errors.length;
      const parts = [
        `${supplierLabel}: ${ok} ordin${ok === 1 ? "e" : "i"} creat${ok === 1 ? "o" : "i"}`,
      ];
      if (ok > 0) {
        const numeri = result.ordered
          .map((o) => o.supplier_order_ref || o.numero)
          .filter(Boolean)
          .join(", ");
        if (numeri) parts.push(`n. ${numeri}`);
        // NIENTE totale qui: «da bonificare» si legge in UN posto solo, la
        // barra della selezione. Averlo anche nell'esito metteva due cifre
        // diverse sullo schermo — «1150,46» (i soli ordini appena creati) e
        // «1422,52» (i costi noti di tutta la selezione) — entrambe giuste e
        // insieme incomprensibili (Davide, 2026-08-10). Se per spiegare la
        // differenza serve una frase, il numero di troppo è uno dei due.
      }
      if (sk) parts.push(`${sk} già ordinati`);
      if (er) parts.push(`${er} errori`);
      let msg = `${parts.join(", ")}.`;
      // Mostra il primo errore reale: "0 creati" senza motivo sembra che non
      // sia successo niente.
      if (er > 0 && result.errors[0]) msg += ` — ${result.errors[0].message}`;
      // Conferma non vista sul portale: l'ordine può non essere partito, e un
      // successo verde lo nasconderebbe finché non se ne accorge il cliente.
      const daVerificare = result.ordered.filter((o) => o.conferma_vista === false);
      if (daVerificare.length > 0) {
        const quali = daVerificare.map((o) => o.reference).join(", ");
        onError(
          `${msg} ⚠️ Conferma non vista sul portale per ${quali}: verifica su my.bizzotto.com prima di riordinare.`,
        );
        setPreview(null);
        onRefresh();
        return;
      }
      if (ok === 0 && (er > 0 || sk === 0)) onError(msg);
      else onMessage(msg);
      setPreview(null);
      onRefresh();
    } catch (err) {
      // Errore dell'invio: resta NEL dialog, così si vede accanto a ciò che
      // si stava confermando.
      setSendError(prettifyApiError(err));
    } finally {
      setSending(false);
    }
  }

  const dialog = preview && (
    <PortalOrderConfirmDialog
      supplierLabel={supplierLabel}
      previews={preview.orders}
      previewErrors={preview.errors}
      sending={sending}
      sendError={sendError}
      onConfirm={() => void confirm()}
      onClose={() => {
        if (!sending) setPreview(null);
      }}
    />
  );

  return { start, busy, dialogBusy: sending || preview !== null, dialog };
}
